import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as goodsSpecTypeApi from '../../api/goodsSpecType';
import { message } from 'antd';
import moment from 'moment';

// 定义输入类型映射
export const INPUT_TYPE_MAP = {
  '单选': 'SINGLE_SELECT',
  '多选': 'MULTI_SELECT',
  '文本': 'TEXT',
  '数字': 'NUMBER'
};

// 定义状态映射
export const STATUS_MAP = {
  '启用': 'ENABLED',
  '禁用': 'DISABLED'
};

// 分页查询商品规格类型
export const fetchGoodsSpecTypes = createAsyncThunk(
  'goodsSpecType/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await goodsSpecTypeApi.listGoodsSpecTypes(params);
      
      if (!response || !response.data) {
        return rejectWithValue('获取商品规格类型列表失败');
      }
      
      return {
        specTypes: response.data.records.map(item => ({
          ...item,
          createTime: moment(new Date(...item.createTime)).format('YYYY-MM-DD HH:mm:ss'),
          updateTime: moment(new Date(...item.updateTime)).format('YYYY-MM-DD HH:mm:ss')
        })),
        total: response.data.total || 0,
        current: response.data.current || 1,
        pageSize: response.data.size || 10,
        pages: response.data.pages || 1
      };
    } catch (error) {
      return rejectWithValue(error.message || '获取商品规格类型列表失败');
    }
  }
);

// 添加商品规格类型
export const addGoodsSpecType = createAsyncThunk(
  'goodsSpecType/add',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await goodsSpecTypeApi.addGoodsSpecType({
        ...data,
        inputType: Object.keys(INPUT_TYPE_MAP).find(key => INPUT_TYPE_MAP[key] === data.inputType) || '单选',
        status: Object.keys(STATUS_MAP).find(key => STATUS_MAP[key] === data.status) || '启用'
      });
      
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '添加商品规格类型失败');
      }
      
      // 重新获取列表
      dispatch(fetchGoodsSpecTypes({ page: 1, pageSize: 10 }));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '添加商品规格类型失败');
    }
  }
);

// 更新商品规格类型
export const updateGoodsSpecType = createAsyncThunk(
  'goodsSpecType/update',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await goodsSpecTypeApi.updateGoodsSpecType({
        ...data,
        inputType: Object.keys(INPUT_TYPE_MAP).find(key => INPUT_TYPE_MAP[key] === data.inputType) || '单选',
        status: Object.keys(STATUS_MAP).find(key => STATUS_MAP[key] === data.status) || '启用'
      });
      
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '更新商品规格类型失败');
      }
      
      // 重新获取列表
      dispatch(fetchGoodsSpecTypes({ page: 1, pageSize: 10 }));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '更新商品规格类型失败');
    }
  }
);

// 删除商品规格类型
export const deleteGoodsSpecType = createAsyncThunk(
  'goodsSpecType/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await goodsSpecTypeApi.deleteGoodsSpecType(id);
      
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '删除商品规格类型失败');
      }
      
      // 重新获取列表
      dispatch(fetchGoodsSpecTypes({ page: 1, pageSize: 10 }));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '删除商品规格类型失败');
    }
  }
);

// 初始状态
const initialState = {
  specTypes: [],
  total: 0,
  current: 1,
  pageSize: 10,
  pages: 1,
  loading: false,
  error: null
};

const goodsSpecTypeSlice = createSlice({
  name: 'goodsSpecType',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchGoodsSpecTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGoodsSpecTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.specTypes = action.payload.specTypes;
        state.total = action.payload.total;
        state.current = action.payload.current;
        state.pageSize = action.payload.pageSize;
        state.pages = action.payload.pages;
      })
      .addCase(fetchGoodsSpecTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload || '获取商品规格类型列表失败');
      })
      
      // 添加
      .addCase(addGoodsSpecType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addGoodsSpecType.fulfilled, (state) => {
        state.loading = false;
        message.success('添加商品规格类型成功');
      })
      .addCase(addGoodsSpecType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload || '添加商品规格类型失败');
      })
      
      // 更新
      .addCase(updateGoodsSpecType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGoodsSpecType.fulfilled, (state) => {
        state.loading = false;
        message.success('更新商品规格类型成功');
      })
      .addCase(updateGoodsSpecType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload || '更新商品规格类型失败');
      })
      
      // 删除
      .addCase(deleteGoodsSpecType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGoodsSpecType.fulfilled, (state) => {
        state.loading = false;
        message.success('删除商品规格类型成功');
      })
      .addCase(deleteGoodsSpecType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload || '删除商品规格类型失败');
      });
  }
});

export default goodsSpecTypeSlice.reducer;
